import { getArtworkRecord } from "../persistence/artworkRepository";
import type { PerfectDrawProject } from "../projects/types";
import {
  BACKUP_FORMAT,
  BACKUP_FORMAT_VERSION,
  BACKUP_MIME_TYPE,
  MAX_ARTWORK_BYTES,
  MAX_BACKUP_FILE_BYTES,
  type PerfectDrawBackupV3,
  validateBackupText,
} from "./backupSchema";
import type { PersistedDocumentState } from "../comic/comicTypes";
import type { StoredRasterLayerBlob } from "../layers/layerTypes";

async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  const chunkSize = 32_768;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return btoa(binary);
}

function safeFilename(name: string): string {
  const sanitized = name
    .normalize("NFKC")
    .replace(/[^a-zA-Z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  return `${sanitized || "perfect-draw-project"}.perfectdraw`;
}

export async function exportProjectBackup(
  project: PerfectDrawProject,
  flushLatestSave: () => Promise<void>,
): Promise<void> {
  await flushLatestSave();
  const record = await getArtworkRecord(project.id);
  if (!record) throw new Error("Save the project before exporting a backup.");
  const state = record.currentDocumentState;
  if (!state) throw new Error("The saved document structure is unavailable.");
  const layers =
    record.currentLayers ??
    (record.currentBlob
      ? state.layers
          .filter((layer) => layer.type === "raster")
          .map((layer, index) => ({
            layerId: layer.id,
            mimeType: "image/png" as const,
            blank: index !== 0,
            blob: index === 0 ? record.currentBlob : undefined,
          }))
      : undefined);
  if (!layers) throw new Error("The saved layer artwork is unavailable.");

  const text = await createProjectBackupText(project, state, layers);
  const blob = new Blob([text], { type: BACKUP_MIME_TYPE });
  if (blob.size > MAX_BACKUP_FILE_BYTES) {
    throw new Error("This project is too large to export as a compatible backup.");
  }
  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = safeFilename(project.name);
    anchor.click();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export async function createProjectBackupText(
  project: PerfectDrawProject,
  state: PersistedDocumentState,
  layers: ReadonlyArray<StoredRasterLayerBlob>,
  exportedAt = new Date().toISOString(),
): Promise<string> {
  const decodedBytes = layers.reduce(
    (total, layer) => total + (layer.blank ? 0 : (layer.blob?.size ?? 0)),
    0,
  );
  if (decodedBytes > MAX_ARTWORK_BYTES) {
    throw new Error("This project is too large to export as a compatible backup.");
  }
  const dialogueLayerId = state.layers.find((layer) => layer.type === "object")?.id;
  const artworkLayers: PerfectDrawBackupV3["artwork"]["layers"] = [];
  // Convert one Blob at a time so ArrayBuffers for different raster layers are
  // never simultaneously retained in addition to the final base64 strings.
  for (const layer of layers) {
    if (!layer.blank && !layer.blob) throw new Error("Saved layer artwork is missing.");
    artworkLayers.push({
      layerId: layer.layerId,
      mimeType: "image/png",
      blank: layer.blank,
      base64: layer.blank ? undefined : await blobToBase64(layer.blob!),
    });
  }
  const backup: PerfectDrawBackupV3 = {
    format: BACKUP_FORMAT,
    formatVersion: BACKUP_FORMAT_VERSION,
    exportedAt,
    project: {
      name: project.name,
      type: project.type,
      orientation: project.orientation,
      width: project.width,
      height: project.height,
      settings: {
        backgroundColor: project.settings.backgroundColor,
        easyMode: project.settings.easyMode,
        pressureEnabled: project.settings.pressureEnabled,
        autosaveEnabled: project.settings.autosaveEnabled,
      },
    },
    activeLayerId: state.activeLayerId,
    layers: state.layers.map((layer) => ({ ...layer })),
    comic:
      project.type === "comic" && state.comicTemplateId && dialogueLayerId
        ? {
            templateId: state.comicTemplateId,
            dialogueLayerId,
            panels: state.panels.map((panel) => ({
              id: panel.id,
              templateSlot: panel.templateSlot,
            })),
            objects: state.objects.map((object) => ({ ...object, data: { ...object.data } })),
          }
        : undefined,
    artwork: {
      layers: artworkLayers,
    },
  };
  const text = JSON.stringify(backup);
  const byteLength = new TextEncoder().encode(text).byteLength;
  if (byteLength > MAX_BACKUP_FILE_BYTES) {
    throw new Error("This project is too large to export as a compatible backup.");
  }
  // This is the final preflight: the exact text about to be downloaded must
  // pass the same parser, schema, PNG, dimension, memory, and size checks used
  // by import. No incompatible backup reaches createObjectURL.
  validateBackupText(text, byteLength);
  return text;
}
